import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/utils';
import { Button } from './button';

type ToastType = 'default' | 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  title?: string;
  description?: string;
  type?: ToastType;
  duration?: number;
}

const ToastContext = React.createContext<{
  toasts: Toast[];
  toast: (options: Omit<Toast, 'id'>) => void;
  dismiss: (id: string) => void;
}>({
  toasts: [],
  toast: () => {},
  dismiss: () => {},
});

const useToast = () => React.useContext(ToastContext);

interface ToastProviderProps {
  children: React.ReactNode;
}

const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  
  const toast = React.useCallback((options: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { id, ...options }]);
    
    if (options.duration !== 0) {
      setTimeout(() => {
        dismiss(id);
      }, options.duration || 5000);
    }
  }, []);
  
  const dismiss = React.useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, toast, dismiss }}>
      {children}
      <ToastViewport />
    </ToastContext.Provider>
  );
};

const ToastViewport = () => {
  const { toasts } = useToast();
  
  return ReactDOM.createPortal(
    <div className="fixed bottom-0 right-0 flex flex-col gap-2 p-4 z-[100] max-w-[420px] w-full">
      {toasts.map(toast => (
        <ToastCard key={toast.id} toast={toast} />
      ))}
    </div>,
    document.body
  );
};

interface ToastCardProps {
  toast: Toast;
}

const ToastCard = ({ toast }: ToastCardProps) => {
  const { dismiss } = useToast();
  
  const typeStyles: Record<ToastType, string> = {
    default: 'bg-background border border-border',
    success: 'bg-green-50 border border-green-200 text-green-800',
    error: 'bg-red-50 border border-red-200 text-red-800',
    warning: 'bg-yellow-50 border border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border border-blue-200 text-blue-800',
  };

  return (
    <div
      className={cn(
        'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-4 pr-6 shadow-lg transition-all data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-bottom-full',
        typeStyles[toast.type || 'default']
      )}
      data-state="open"
    >
      <div className="grid gap-1">
        {toast.title && <div className="text-sm font-semibold">{toast.title}</div>}
        {toast.description && <div className="text-sm opacity-90">{toast.description}</div>}
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={() => dismiss(toast.id)}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

const toast = (options: Omit<Toast, 'id'>) => {
  // Global toast function
};

export { ToastProvider, useToast, toast };
export type { Toast, ToastType };